/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router';
import { TABLE_COLUMNS, NAV_PAGE_LIST } from './constants';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { useInjectReducer } from '../../utils/injectReducer';
import reducer from './reducer';
import { getIsUsersRolesListLoadingSelector, getPaginationSelector, getUsersRolesListSelector } from './selectors';
import { getUsersRolesListAction, resetStateAction } from './actions';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { getIsAvailableUsersRolesButtonsSelector } from '../profile/selectors';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const UsersRolesContainerWrapper = ({
	onGetUsersRolesList,
	usersRolesList,
	isUsersRolesListLoading,
	pagination,
	onResetState,
	isAvailable,
}) => {
	useInjectSaga({ key: 'usersRolesContainerSaga', saga });
	useInjectReducer({ key: 'usersRolesContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	useEffect(() => {
		onGetUsersRolesList({ searchQuery: removeModalQuery(query.toString()) });

		return () => onResetState();
	}, []);

	const handleAddButtonClick = () => {
		query.set('modal', 'user_role');
		query.set('mode', 'add');

		history.push(`${pathname}?${query}`);
	};

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetUsersRolesList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetUsersRolesList({ searchQuery: removeModalQuery(query.toString()) });
	};

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetUsersRolesList({ searchQuery: removeModalQuery(query.toString()) });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS()));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetUsersRolesList({ searchQuery: removeModalQuery(query.toString()) });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={NAV_PAGE_LIST}
			pageContent={
				<DataGridComponent
					headerText="Роли пользователей"
					onAddButtonClick={isAvailable.create ? handleAddButtonClick : null}
					columns={TABLE_COLUMNS()}
					rows={usersRolesList}
					pagination={
						!pagination
							? null
							: { ...pagination, onPageChange: handlePageChange, onSelectChange: handleSelectChange }
					}
					isTableDataLoading={isUsersRolesListLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	usersRolesList: getUsersRolesListSelector(),
	isUsersRolesListLoading: getIsUsersRolesListLoadingSelector(),
	pagination: getPaginationSelector(),
	isAvailable: getIsAvailableUsersRolesButtonsSelector(),
});

const mapDispatchToProps = {
	onGetUsersRolesList: getUsersRolesListAction,
	onResetState: resetStateAction,
};

const UsersRolesContainer = connect(mapStateToProps, mapDispatchToProps)(UsersRolesContainerWrapper);

UsersRolesContainerWrapper.propTypes = {
	onGetUsersRolesList: PropTypes.func.isRequired,
	usersRolesList: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			name: PropTypes.string.isRequired,
			count: PropTypes.number,
		}),
	),
	isUsersRolesListLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	isAvailable: PropTypes.objectOf(PropTypes.bool),
};

export default UsersRolesContainer;
